import { useEffect, useState } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useFormik } from "formik";
import Select from 'react-select';
import TextInput from "../../common/textfield/TextInput";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../common/Button";
import { failed, success } from "../../common/Toastify";
import { FormikObjEdit, categoryOptions, filterStatusOptions, handleExt, pagesOptions } from "./Constant";
import SelectFiled from "../../common/textfield/SelectFiled";
import { Tooltip } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { getPractitionerAuthorizedList } from "../../api/Individual";
import { getLocationQuery, getOrgnizationForLocation } from "../../api/ClinicLocation";
import { decryptData } from "../../EncryptDecrypt";
import { editDocumentDashboard } from "../../api/FillableDocument.js";
import { getOrganization } from "../../api/Organization";
import PatientSelectModal from "../../common/components/PatientSelectModal.js";

function EditDocument() {
  const navigate = useNavigate();
  const location = useLocation();
  const userRoles = useSelector((state) => state?.auth?.user?.["cognito:groups"]);
  const user = useSelector((state) => state?.auth?.user);

  const [btnLoading, setBtnLoading] = useState(false);
  const [practionerList, setPractionerList] = useState([]);
  const [locations, setLocations] = useState([]);

  const [practitionerValue, setPractitionerValue] = useState({});
  const [patientValue, setPatientValue] = useState({});
  const [locationValue, setLocationValue] = useState({});
  const [showPatientSelectionModal, setShowPatientSelectionModal] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState(null)

  const [orgName, setOrgName] = useState("");
  const [listsLoading, setListsLoading] = useState({
    locations: false,
    practitioners: false,
    patients: false,
  });

  const {
    id,
    author,
    category,
    custodian,
    numPages,
    authenticator,
    subject,
    status,
    fromDocumentDashboard,
    submittedBy,
  } = location?.state || {};

  const custRef = custodian?.reference; // "Organization/017187"
  const orgId = custRef?.split("/")?.[1] || null;

  // disable "New" in status
  const filteredStatus = filterStatusOptions?.map((data) => ({
    ...data,
    disabled: data.name === "New",
  }));

  const formik = useFormik({
    ...FormikObjEdit,
    onSubmit: async (values) => {
      values.firstName = values?.firstName?.trim();
      values.lastName = values?.lastName?.trim();
      values.healthCardNumber = values?.healthCardNumber?.trim();
      setBtnLoading(true);
      try {
        if (submittedBy?.coding?.[0]?.code === "fax") {
          values.locationId = "";
          values.locationName = "";
        }
        const res = await editDocumentDashboard({
          ...values,
          id,
          isForSRFax: true,
          orgId, // locked to document org
          loginPractitionerName: user?.name?.[0]?.text.trim(),
        });
        if (res.status === true) {
          fromDocumentDashboard
            ? navigate("/app/document-dashboard")
            : navigate("/app/dashboard", { state: { activeTab: true } });
          success(res.message);
        } else {
          failed(res.error);
        }
      } catch (err) {
        failed(err?.response?.data?.message || err?.response?.data?.error || err.message);
      } finally {
        setBtnLoading(false);
      }
    },
  });

  const handleSource = (e) => {
    setLocationValue(e || null);
    formik.setFieldValue("locationId", e?.value ? e.value : "");
    formik.setFieldValue(
      "locationName",
      e?.value === "other" ? "Other" : locations?.find((o) => o?.value === e?.value)?.label || ""
    );
  };

  const handleCategory = (e) => {
    formik.setFieldValue("categoryCode", e.target.value);
    formik.setFieldValue(
      "categoryDisplay",
      categoryOptions?.find((o) => o?.value === e.target.value)?.name
    );
  };

  const handlePractitionerChange = (e) => {
    setPractitionerValue(e || null);
    formik.setFieldValue("practitionerId", e?.value ? e.value : "");
    formik.setFieldValue(
      "selectedPractitionerName",
      e?.value ? practionerList?.find((o) => o?.value === e?.value)?.label : ""
    );
    if (status === "new") {
      formik.setFieldValue("status", e?.value ? "assigned" : "new");
    } else {
      formik.setFieldValue("status", e?.value ? status : "new");
    }
  };


  const handleStatusChange = (e) => {
    if (e.target.value === "new") {
      formik.setFieldValue("status", "assigned");
    } else {
      formik.setFieldValue("status", e.target.value);
    }
  };

  const handlePagesChange = (e) => {
    let inputValue = e.target.value;
    if (inputValue.startsWith("0") && inputValue !== "0") {
      const newValue = inputValue.replace(/^0+/, "");
      formik.setFieldValue("pages", newValue);
    } else if (inputValue === "0") {
      formik.setFieldValue("pages", "");
    } else {
      formik.setFieldValue("pages", inputValue);
    }
  };

  // ---------- Fetchers bound to document org ----------
  const fetchOrgName = async () => {
    if (!orgId) return;
    try {
      const res = await getOrganization(orgId);
      if (res?.data) {
        setOrgName(res.data.name);
        formik.setFieldValue("organizationId", orgId);
        formik.setFieldValue("organizationName", res.data.name);
      }
    } catch (err) {
      console.warn(`Couldn’t load org details for ${orgId}`, err);
    }
  };

  const fetchLocationsForOrg = async () => {
    if (!orgId || submittedBy?.coding?.[0]?.code === "fax") {
      setLocations([]);
      return;
    }
    setListsLoading((s) => ({ ...s, locations: true }));
    try {
      await getOrgnizationForLocation();
      const data = await getLocationQuery({ orgnizationIds: [orgId], userRoles });
      const opts =
        (data?.data || [])
          .map((obj) => ({
            id: obj?.id,
            value: obj?.id,
            label: obj?.name
              ?.split(" ")
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(" "),
          }))
          .sort((a, b) => a.label.localeCompare(b.label)) || [];
      setLocations([...opts, { id: "other", value: "other", label: "Other" }]);
    } catch (e) {
      console.log("error fetching locations", e);
      setLocations([]);
    } finally {
      setListsLoading((s) => ({ ...s, locations: false }));
    }
  };

  const fetchPractitionersForOrg = async () => {
    if (!orgId) {
      setPractionerList([]);
      return;
    }
    setListsLoading((s) => ({ ...s, practitioners: true }));
    try {
      const res = await getPractitionerAuthorizedList(orgId);
      const practs = res?.result;
      let unsorted = practs.filter(item => item.name || false);
      setPractionerList(
        unsorted
          ?.map((ele) => ({
            id: ele.id,
            label: decryptData(
              ele.name[0]?.text
                ?.split(" ")
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(" ")
            ),
            value: ele.id,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)) || []
      );
    } catch (e) {
      console.log("error fetching practitioner list", e);
      setPractionerList([]);
    } finally {
      setListsLoading((s) => ({ ...s, practitioners: false }));
    }
  };

  // ---------- Init + seed form ----------
  useEffect(() => {
    // org & lists (locked to doc org)
    fetchOrgName();
    fetchLocationsForOrg();
    fetchPractitionersForOrg();

    // seed current values
    const seededPatient =
      subject?.reference
        ? {
            id: subject.reference.split("/")[1],
            label: subject.display,
            value: subject.reference.split("/")[1],
          }
        : null;
    if (seededPatient?.label) {
      setSelectedPatient({
        id: seededPatient?.id,
        display: seededPatient?.label
      })
    }

    const seededPractitioner =
      authenticator?.[0]?.reference
        ? {
            id: authenticator[0].reference.split("/")[1],
            label: authenticator[0].display,
            value: authenticator[0].reference.split("/")[1],
          }
        : null;

    const seededLocation =
      submittedBy?.coding?.[0]?.code !== "fax" && author?.[0]?.reference
        ? {
            id: author[0].reference.split("/")[1],
            label: author[0].display,
            value: author[0].reference.split("/")[1],
          }
        : null;

    setPatientValue(seededPatient || null);
    setPractitionerValue(seededPractitioner || null);
    setLocationValue(seededLocation || null);

    if (submittedBy?.coding?.[0]?.code !== "fax") {
      formik.setFieldValue("locationId", author?.[0]?.reference?.split("/")[1] || "");
    } else {
      formik.setFieldValue("locationId", author?.[0]?.display || "");
    }
    formik.setFieldValue("locationName", author?.[0]?.display || "");
    formik.setFieldValue("categoryCode", category?.[0]?.coding?.[0]?.code || "");
    formik.setFieldValue("categoryDisplay", category?.[0]?.coding?.[0]?.display || "");
    formik.setFieldValue("pages", numPages || "");
    formik.setFieldValue("status", status || "");
    formik.setFieldValue("selectedPractitionerName", authenticator?.[0]?.display || "");
    formik.setFieldValue("assignPatient", subject?.display || "");
    formik.setFieldValue("practitionerId", authenticator?.[0]?.reference?.split("/")[1] || "");
    formik.setFieldValue("patientId", subject?.reference?.split("/")[1] || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="common-listing">
      <div className="heading-wrap mb-3">
        <Tooltip title="Go back">
          <div
            style={{ marginRight: "1rem", cursor: "pointer" }}
            onClick={() =>
              fromDocumentDashboard
                ? navigate("/app/document-dashboard")
                : navigate("/app/dashboard", { state: { activeTab: true } })
            }
          >
            <ArrowBack />
          </div>
        </Tooltip>
        <h1>Document Details</h1>
      </div>

      <div className="custom-card p-4">
        <form className="common-form border-fields" onSubmit={formik.handleSubmit}>
          <Row>
            {/* ORG is locked/read-only */}
            <Col>
              <TextInput
                keyField="orgName"
                label="Organization"
                type="text"
                formik={formik}
                value={orgName}
                disabled={true}
                placeholder="Organization"
              />
            </Col>

            <Col>
              {submittedBy?.coding?.[0]?.code !== "fax" ? (
                <div className="field-wrap">
                  <label className="searchSelectLabel">
                    Source <span style={{ color: "red" }}> *</span>
                  </label>
                  <Select
                    onChange={handleSource}
                    isMulti={false}
                    name="locationId"
                    options={locations}
                    value={locationValue}
                    className="search-select"
                    classNamePrefix="select"
                    isClearable={true}
                    isLoading={listsLoading.locations}
                  />
                  <div style={{ marginTop: "16px" }}>
                    {formik?.touched["locationId"] && formik?.errors["locationId"] ? (
                      <div className="error-text">{formik?.errors["locationId"]}</div>
                    ) : null}
                  </div>
                </div>
              ) : (
                <TextInput
                  keyField={"locationId"}
                  label={"Source"}
                  type="text"
                  formik={formik}
                  placeholder={"Source"}
                  disabled={true}
                />
              )}
            </Col>

            <Col>
              <SelectFiled
                keyField={"categoryCode"}
                label={"Category"}
                formik={formik}
                options={categoryOptions}
                onChange={handleCategory}
              />
            </Col>

            <Col>
              <TextInput
                keyField={"pages"}
                label={"Pages"}
                type="text"
                formik={formik}
                placeholder={"Pages"}
                onKeyPress={handleExt}
                onChange={handlePagesChange}
                autoComplete="off"
                required={false}
              />
            </Col>

            <Col>
              <SelectFiled
                keyField={"status"}
                label={"Status"}
                formik={formik}
                options={filteredStatus}
                onChange={handleStatusChange}
                disabled={status === "new"}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <div className="field-wrap">
                <label className="searchSelectLabel">Assign Practitioner (Optional)</label>
                <Select
                  onChange={handlePractitionerChange}
                  isMulti={false}
                  name="practitionerId"
                  options={practionerList}
                  className="search-select"
                  classNamePrefix="select"
                  value={practitionerValue}
                  isClearable={true}
                  isLoading={listsLoading.practitioners}
                />
                <div>
                  {formik?.touched["practitionerId"] && formik?.errors["practitionerId"] ? (
                    <div className="error-text">{formik?.errors["practitionerId"]}</div>
                  ) : null}
                </div>
              </div>
            </Col>

            <Col>
               <div className="field-wrap">
                <label htmlFor="patient" className="form-label">
                  Assign Patient (Optional)
                </label>
                <Button
                    // width="100%"
                    className="w-100"
                    type="button"
                    variant="secondary"
                    onClick={() => setShowPatientSelectionModal(true)}
                >
                    {selectedPatient
                    ? `${selectedPatient.display}`
                    : "Select Patient"}
                </Button>
              </div>
            </Col>
          </Row>

          <div className="btn-wrap" style={{ display: "flex", marginTop: "45px" }}>
            <Button
              onClick={() =>
                fromDocumentDashboard
                  ? navigate("/app/document-dashboard")
                  : navigate("/app/dashboard", { state: { activeTab: true } })
              }
              variant="secondary"
              title="Cancel"
            >
              Cancel
            </Button>
            <Button type="submit" isLoading={btnLoading}>
              Save
            </Button>
          </div>
        </form>
      </div>
      {/* Patient select modal */}
      <PatientSelectModal
        show={showPatientSelectionModal}
        orgId={orgId}
        onHide={() => setShowPatientSelectionModal(false)}     
        onPatientSelected={(patient) => {
        formik.setFieldValue("patientId", patient?.id)
        formik.setFieldValue("assignPatient", patient?.name?.[0]?.text?.split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ') || "(Unnamed)")
        setShowPatientSelectionModal(false)
        setSelectedPatient({
          id: patient?.id,
          display: patient?.name?.[0]?.text?.split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ') || "(Unnamed)",
          })
        }}
      />
    </section>
  );
}

export default EditDocument;
